
import { Options, prop, Vue } from "vue-class-component";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { PrintReceiptController } from "@/app/ui/controllers/PrintReceiptController";
import {
  CreateInvoiceApiRequest,
  ListBookingHistoryDetailApiRequest
} from "@/data/payload/api/PrintReceiptApiRequest";
import { BookingController } from "@/app/ui/controllers/BookingController";
import {
  BookingFormType,
  CustomerBookingV3
} from "@/domain/entities/CreateBookingV3";
import { debounce } from "lodash";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { CustomerListEntities } from "@/domain/entities/Booking";
import SelectCustomerV2 from "@/app/ui/components/select-customer/index.vue";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import {
  Invoice,
  InvoiceDetail,
  SttHistorySuccessBooking,
  SuccessInvoiceData
} from "@/domain/entities/PrintReceipt";
import SendInvoiceReceiverRows from "@/app/ui/views/shipment/print-receipt-v2/components/SendInvoiceReceiverRows.vue";
import InvoicePdfA4 from "../../../../print-receipt-v2/components/invoice/InvoicePdfA4.vue";
import InvoicePdfStruk from "../../../../print-receipt-v2/components/invoice/InvoicePdfStruk.vue";
import formatPrice from "@/app/infrastructures/misc/common-library/v2/FormatPrice";

type CustomerParams = {
  search: string;
  isSender?: boolean;
};

class Props {
  formType = prop<string>({
    default: BookingFormType.MANUAL,
    type: String,
    required: true
  });
}

@Options({
  components: {
    SelectCustomerV2,
    SendInvoiceReceiverRows,
    InvoicePdfA4,
    InvoicePdfStruk
  },
  watch: {
    visible(newVal: boolean) {
      if (newVal) this.init();
    }
  }
})
export default class ModalPreviewInvoice extends Vue.with(Props) {
  get visible() {
    return PrintReceiptController.visiblePreviewInvoice;
  }
  set visible(val: boolean) {
    PrintReceiptController.setVisiblePreviewInvoice(val);

    if (!val) {
      PrintReceiptController.resetCreateInvoiceFromSuccessBooking();
      this.form = new CreateInvoiceApiRequest();
    }
  }

  init() {
    this.getList();
  }

  get accountCurrency() {
    return AccountController.accountData.accountCurrency;
  }

  listSttBookingHistoryDetail: SttHistorySuccessBooking[] = [];
  get listSttNumberLocalStorage() {
    const list = PrintReceiptController.listSttSuccessBooking;
    const selectedSttHistoryBooking = PrintReceiptController.selectedSttHistorySuccessBooking.map(
      (item) => item.sttNo
    );

    return PrintReceiptController.processInvoiceFromSuccessBooking
      ? [list[0]]
      : selectedSttHistoryBooking;
  }
  get latestSttBookingHistoryDetail(): SttHistorySuccessBooking {
    return this.listSttBookingHistoryDetail.length
      ? this.listSttBookingHistoryDetail[0]
      : new SttHistorySuccessBooking();
  }
  get invoiceSummaryData() {
    const totalTariff = this.listSttBookingHistoryDetail.reduce(
      (total, item) => total + Number(item.tariff || 0),
      0
    );
    const totalInvoice = this.listSttBookingHistoryDetail.reduce(
      (total, item) => total + Number(item.amount || 0),
      0
    );

    return {
      total: this.listSttBookingHistoryDetail.length,
      tariff: formatPrice(totalTariff, "", this.accountCurrency),
      amount: formatPrice(totalInvoice, "", this.accountCurrency)
    };
  }
  async getList() {
    try {
      MainAppController.showLoading();

      const res = await PrintReceiptController.getBookingHistoryDetail(
        new ListBookingHistoryDetailApiRequest({
          sttNumbers: this.listSttNumberLocalStorage
        })
      );
      this.listSttBookingHistoryDetail = res.data;
      this.onSelectCustomer(
        new CustomerBookingV3({
          customerName: this.latestSttBookingHistoryDetail.senderName,
          customerPhoneNumber: this.latestSttBookingHistoryDetail.senderPhone
        })
      );
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          err,
          this.$t("bookingV4.previewInvoice.errorGetHistory") as string,
          this.getList
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  form = new CreateInvoiceApiRequest();

  get isBookingClient() {
    return this.formType === BookingFormType.CLIENT;
  }

  // sender name
  get loadingCustomerList(): boolean {
    return BookingController.isLoadingCustomer;
  }
  get customerList() {
    return BookingController.senderCustomerList.data;
  }
  fetchCustomer = debounce((search: string) => {
    this.getCustomerList({
      search,
      isSender: true
    });
  }, 1000);
  get customerRef() {
    return {
      refType:
        !AccountController.accountData.isPosAccount &&
        this.isBookingClient &&
        this.latestSttBookingHistoryDetail.senderName
          ? "client"
          : "",
      refId: AccountController.accountData.isPosAccount
        ? 0
        : this.latestSttBookingHistoryDetail.senderName ?? 0
    };
  }
  getCustomerList(params?: CustomerParams) {
    BookingController.getCustomerList({
      ...params,
      cache: true,
      search: params?.search || "",
      refType: this.customerRef.refType,
      refId: Number(this.customerRef.refId)
    });
  }
  onFocusCustomer() {
    this.getCustomerList({
      search: this.form.billedToName
    });
  }
  onChangeName(value: string) {
    this.form.billedToName = value;

    if (!value || value.length >= 3) {
      this.fetchCustomer(value);
    }
  }
  onSelectCustomer(item: CustomerBookingV3) {
    this.form.billedToName = item.customerName;
    this.form.billedToPhone = `${item.customerPhoneCode}${item.customerPhoneNumber}`;

    this.form.billedTo = `${this.form.billedToName} (${this.form.billedToPhone})`;
  }
  async onScroll() {
    const nextPage = BookingController.senderCustomerList.pagination.page + 1;
    const data = await BookingController.getCustomerInfiniteScrollList({
      cache: true,
      search: this.form.billedToName,
      refType: this.customerRef.refType,
      refId: Number(this.customerRef.refId),
      isSender: false,
      page: nextPage
    });

    BookingController.setSenderCustomerList(
      new CustomerListEntities(data.pagination, [
        ...this.customerList,
        ...data.data
      ])
    );
  }

  // Dropdown options
  viaOptions = [
    { name: "Email", value: "email" },
    { name: "WhatsApp", value: "whatsapp" }
  ];

  get formatOptions() {
    return [
      {
        name: this.$t("bookingV4.previewInvoice.options.pdfA4") as string,
        value: "pdf_a4"
      },
      {
        name: this.$t("bookingV4.previewInvoice.options.pngA4") as string,
        value: "png_a4"
      },
      {
        name: this.$t("bookingV4.previewInvoice.options.pdfStruk") as string,
        value: "pdf_struk"
      }
    ];
  }

  // Receivers state from child component
  receiversData: any = {
    receivers: [],
    validReceivers: [],
    isValid: false
  };

  onReceiversUpdate(data: any) {
    this.receiversData = data;
  }

  get disabled() {
    return !this.form.billedToName || !this.receiversData.isValid;
  }

  invoiceDetail = new Invoice();
  async onCreate() {
    try {
      MainAppController.closeErrorMessage();
      MainAppController.showLoading();

      // create invoice
      const response: ResponsePayloadV2 = await PrintReceiptController.create(
        new CreateInvoiceApiRequest({
          ...this.form,
          sttNumbers: this.listSttNumberLocalStorage
        })
      );

      // get invoice detail
      const detail = await PrintReceiptController.getDetailInvoice(
        response.data.id
      );
      this.invoiceDetail = new Invoice({
        ...detail,
        header: new InvoiceDetail({
          ...detail.header,
          invoiceId: response.data.id
        })
      });

      PrintReceiptController.setInvoiceDetailData(this.invoiceDetail);

      // send invoice
      if (this.receiversData.validReceivers?.length && !this.disabled) {
        await this.onSendInvoice();
      }

      PrintReceiptController.setSuccessCreateInvoiceData(
        new SuccessInvoiceData({
          invoiceId: this.invoiceDetail.header.invoiceId,
          invoiceNo: this.invoiceDetail.header.invoiceNo,
          bookedBy: this.invoiceDetail.header.billedToName,
          bookedByPhone: this.invoiceDetail.header.billedToPhone,
          totalStt: this.invoiceDetail.header.totalStt,
          totalAmount: this.invoiceDetail.header.totalAmount,
          balanceDue: this.invoiceDetail.header.balanceDue
        })
      );

      this.visible = false;
      PrintReceiptController.setSuccessCreateInvoice(true);
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("bookingV4.previewInvoice.errorCreateInvoice") as string,
          this.onCreate
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  async onSendInvoice() {
    const pdf = await (this.$refs as any).invoice.generatePdfA4();
    const png = await (this.$refs as any).invoice.generatePngA4();
    const pdfStruk = await (this.$refs as any).struk.generatePdfStruk();

    PrintReceiptController.processSendInvoice({
      validReceivers: this.receiversData.validReceivers,
      invoiceId: this.invoiceDetail.header.invoiceId,
      invoiceNo: this.invoiceDetail.header.invoiceNo,
      pdf,
      pdfStruk,
      png
    });
  }
}
